import React from 'react';
import Helmet from 'react-helmet';
import { useConfig } from 'ha/helpers/config';
import { useServices } from 'ha/services';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'ha/i18n';
import { LISTING_DETAIL_PAGE, HOME, SEARCH } from 'ha/constants/pageNames';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';
import HttpStatus from 'ha/constants/HttpStatus';
import { EventName, trackAudience } from 'ha/utils/unless';

import { getIsAuthenticated } from '../AuthLogic/selectors';
import { AuthLogicActions, fetchUser } from '../AuthLogic/actions';

import { sendAlert } from '../Alertbar/actions';
import { usePage } from '../Page/contexts/PageContext';
import { AlertType } from '../Alertbar/types';

const GoogleOneTapCallback = 'GoogleOneTapCallback';
const GOOGLE_ONE_TAP_EVENT = 'Clicked on Google One Tap';

interface GoogleOneTapResponse {
  clientId: string;
  credential: string;
  select_by: string;
}

export default function GoogleOneTap() {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const isAuthenticated = Boolean(useSelector(getIsAuthenticated));
  const { google } = useConfig();
  const { apiV2 } = useServices();
  const { T } = useIntl();
  const { name: pageName, category: pageCategory } = usePage();
  const checkPage =
    pageCategory === LISTING_DETAIL_PAGE ||
    pageCategory === SEARCH ||
    pageName === HOME;

  React.useEffect(() => {
    const callback = async (response: GoogleOneTapResponse) => {
      try {
        const { status } = await apiV2.googleOneTapLogin(response.credential);
        trackEvent(GOOGLE_ONE_TAP_EVENT);

        if (status === HttpStatus.CREATED) {
          trackAudience(EventName.PROFILE_MADE);
        }

        dispatch(fetchUser());
        // Logged in
      } catch (error) {
        // Not Logged in
        const errorMessage = T('We could not connect to your Google account');

        dispatch(
          sendAlert({
            text: errorMessage,
            kind: AlertType.Danger,
            dismissAfter: 10000,
          }),
        );
        dispatch(AuthLogicActions.authFail());
      }
    };

    window[GoogleOneTapCallback] = callback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!checkPage || isAuthenticated) {
    return null;
  }

  return (
    <React.Fragment>
      <Helmet>
        <script src="https://accounts.google.com/gsi/client" async defer />
      </Helmet>
      <div
        id="g_id_onload"
        data-client_id={google.client_id}
        data-callback={GoogleOneTapCallback}
      />
    </React.Fragment>
  );
}
